<script lang="ts" setup>
import { computed, ref } from "vue";
import HouseChatList from "./HouseChatList.vue";
import { useHouseSession } from "../store/houseSession";
import { showConfirmDialog } from "vant";
import { useHouseMessageList } from "../composable/useHouseMessageList";
import SubjectPanel from "./panel/subject/SubjectPanel.vue";
import { useGlobalUser } from "@/stores/globalUser";

withDefaults(
  defineProps<{
    isPostingMsg?: "start" | "end" | false;
  }>(),
  {
    isStreamReplying: false,
    streamMsg: "",
    myMessage: "",
    isPostingMsg: false,
  }
);

const emit = defineEmits<{
  (e: "enable-all-active-thought"): void;
}>();

const { messageList } = useHouseMessageList({});

const houseStore = useHouseSession();

const clickClear = async () => {
  if (!houseStore.houseSession) return;

  try {
    if (houseStore.houseSession.subject) {
      await showConfirmDialog({
        message: "确定要清空所有对话吗？",
        confirmButtonText: "确定",
        cancelButtonText: "取消",
      });
    }
  } catch {
    return;
  }

  await houseStore.deleteHouseSessionMessage();
};

const description = computed(() => {
  if (houseStore.houseSession?.mateSpriteIds.length) {
    // 有 mate
    return "请先在小屋中点击成员->打开【💬想法】开关，即可开启对话哦～";
  } else {
    return "请先在小屋中添加成员->点击成员->打开【💬想法】开关，即可开启对话哦～";
  }
});

const isUpdatingSubjectAutoUpdate = ref(false);
const onSwitchSubjectAutoUpdate = async (enabled: boolean) => {
  isUpdatingSubjectAutoUpdate.value = true;
  try {
    await houseStore.updateHouseSessionIsAutoUpdateTopic(enabled);
  } finally {
    isUpdatingSubjectAutoUpdate.value = false;
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[1fr] overflow-hidden">
    <HouseChatList :is-posting-msg="isPostingMsg" class="px-2 py-2">
      <template #header>
        <div class="">
          <div>
            <div class="mb-2">
              <div class="flex justify-between items-center mb-1">
                <div class="red-dot ml-3">话题</div>
                <div class="flex items-center gap-2">
                  <button
                    class="btn btn-xs btn-outline rounded"
                    @click="houseStore.showEditSubjectPopup = true"
                    v-if="messageList.length"
                  >
                    <i class="iconify mingcute--pencil-3-line" />
                    {{ $t("Buttons.edit") }}
                  </button>
                  <div
                    class="border flex items-center gap-1 text-xs p-1 rounded"
                  >
                    话题自动更新
                    <VanSwitch
                      size="small"
                      :model-value="houseStore.houseSession?.isAutoUpdateTopic"
                      @change="onSwitchSubjectAutoUpdate"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div class="text-sm text-base-content/60 px-2">
                  {{ houseStore.houseSession?.subject }}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div>
              <div class="flex justify-between items-center mb-2">
                <div class="red-dot ml-3">全部</div>
                <button
                  class="btn btn-xs btn-outline"
                  @click="clickClear"
                  v-if="messageList.length"
                >
                  <i class="iconify mingcute--delete-2-line" />
                  {{ $t("Buttons.clear") }}
                </button>
              </div>
            </div>

            <div
              v-if="!messageList.length"
              class="text-center text-sm text-base-content/60"
            >
              <template v-if="houseStore.isThoughtActivated">
                <div
                  class="loading loading-ring loading-sm text-base-content"
                />
                <p>正在加载新的对话...</p>
              </template>
              <template v-else>
                <VanEmpty
                  image-size="100"
                  :description="description"
                  class="pb-2"
                />
                <div class="flex items-center justify-center gap-1">
                  也可以点击<button
                    class="btn btn-xs btn-primary rounded"
                    @click="emit('enable-all-active-thought')"
                  >
                    💬 启用想法
                  </button>
                  ，开启启用全部成员的想法
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </HouseChatList>
    <VanPopup
      v-model:show="houseStore.showEditSubjectPopup"
      class="py-5 px-2 rounded"
      position="bottom"
      closeable
    >
      <div class="red-dot ml-6">话题</div>
      <SubjectPanel @save="houseStore.showEditSubjectPopup = false" />
    </VanPopup>
  </div>
</template>
